/**
 * Function that takes a number or string of number and check if given amount is within $1 and $20,500
 *
 * @param {number|string} amount - Number or string of number
 * @param {number} min - Minimum amount
 * @param {number} max - Maximum amount
 * @returns {boolean} - true if given amount is within acceptable amount range, else return false
 */
export const isAcceptableAmount = (amount, min = 1, max = 20500) => {
  const num = Number(amount);
  return num >= min && num <= max;
};

/**
 * Function that takes a number and converts into a currency (with a $ symbol and 2 decimal places).
 * @param {number} num - Number that should be formatted.
 * @returns {number} - String representing the given number with 2 digits after the decimal point.
 */
export const formatAsCurrency = (num) => {
  if (num < 0) {
    // if num is negative, add - sign before $ sign
    return `-$${Math.abs(Math.round(num * 100) / 100).toFixed(2)}`;
  }
  return `$${(Math.round(num * 100) / 100).toFixed(2)}`;
};

/**
 * Function that checks whether the given string represents a dollar amount by performing a regex test.
 * To be valid, the string must not contain a $ infront, allow only one decimal point, and up to two figures after the decimal.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the given string matches the currency regex pattern, or false otherwise.
 */
export const isValidCurrency = (str) => {
  const re = new RegExp('^[0-9]+(.[0-9]{1,2})?$');
  return re.test(str);
};

/**
 * Function that checks whether the given string is a valid provider fee by checking if the string represents a valid currency over $1.00.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string represents a valid currency over $1.00.
 */
export const isValidProviderFee = (str) => {
  const validCurrency = isValidCurrency(str);

  if (validCurrency) {
    return parseFloat(str) > 1;
  }
  return false;
};

/**
 * Function that prevents entry of certain characters into a numeric input field, to assist with accepting currency related values only.
 *
 * NOTE: To be used with an input field with type 'number'.
 *
 * @param {KeyboardEvent} event - The keyboard event.
 * @returns {boolean} - True if character entered is not 'e', '+' and '-'.
 */
export const preventInvalidCurrencyInput = (event) => {
  const CHAR_E = 69;
  const CHAR_PLUS_SIGN = 187;
  const CHAR_MINUS_SIGN = 189;
  const bannedKeyCodes = [CHAR_E, CHAR_PLUS_SIGN, CHAR_MINUS_SIGN];
  return bannedKeyCodes.includes(event.keyCode) ? event.preventDefault() : true;
};
